import { useState } from 'react';
import { api } from '../../lib/api';
import { melbourneToday } from '../../../shared/dates';
import { gstFromGross } from '../../../shared/finance';
import { EXPENSE_CATEGORIES, type ExpenseWithNames, type ProjectWithClient } from '../../../shared/types';
import { DateField, formatMoneyExact, Modal, parseMoney, SelectField, Seg, TrashAction } from '../../components/primitives';
import { reportError } from '../../lib/errorEvents';
import { useIsMobile } from '../../lib/useIsMobile';
import { TrashIcon } from '../../components/icons';
import { Sheet, SheetRow } from '../../components/Sheet';
import { categoryLabel } from '../../lib/financeListMeta';


// ── Expense modal (09-new-expense; doubles as the row editor) ─────────────

export function ExpenseModal({
  initial,
  projects,
  onClose,
  onSaved,
}: {
  initial: ExpenseWithNames | null;
  projects: ProjectWithClient[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isMobile = useIsMobile();
  /** ≤880px: the danger row sits in the body, so its confirm lives here. */
  const [confirmTrash, setConfirmTrash] = useState(false);
  const [trashBusy, setTrashBusy] = useState(false);
  const [trashError, setTrashError] = useState<string | null>(null);
  const today = melbourneToday();
  const [form, setForm] = useState({
    vendor: initial?.vendor ?? '',
    category: initial?.category ?? 'general',
    description: initial?.description ?? '',
    amount: initial ? (initial.amount_cents / 100).toString() : '',
    gstMode: (initial && initial.gst_cents === 0 ? 'free' : 'incl') as 'incl' | 'free',
    expense_date: (initial?.expense_date ?? today) as string | null,
    project_id: initial?.project_id ?? null,
    receipt_url: initial?.receipt_url ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cents = parseMoney(form.amount);

  const submit = async () => {
    if (busy || cents === null || !form.expense_date) return;
    setBusy(true);
    setError(null);
    const payload = {
      vendor: form.vendor || null,
      category: form.category,
      description: form.description || null,
      amount_cents: cents,
      gst_cents: form.gstMode === 'free' ? 0 : gstFromGross(cents),
      expense_date: form.expense_date,
      project_id: form.project_id,
      receipt_url: form.receipt_url || null,
    };
    try {
      if (initial) {
        await api(`/api/admin/finance/expenses/${initial.id}`, { method: 'PATCH', json: payload });
      } else {
        await api('/api/admin/finance/expenses', { method: 'POST', json: payload });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save expense.');
      setBusy(false);
    }
  };

  const trash = async () => {
    await api(`/api/admin/finance/expenses/${initial!.id}`, { method: 'DELETE' });
    onSaved();
    onClose();
  };
  /** The mobile confirm sheet stays open on failure and says why, instead
      of leaving an unhandled rejection behind a closed sheet (review C-5). */
  const confirmTrashMobile = async () => {
    if (trashBusy) return;
    setTrashBusy(true);
    setTrashError(null);
    try {
      await trash();
    } catch (err) {
      setTrashError(err instanceof Error ? err.message : 'Could not move the expense to trash.');
      setTrashBusy(false);
    }
  };

  // ── Field bodies, shared by both widths (§8.2 E) ───────────────────────
  const vendorField = (
    <>
      <span className="f-label">Vendor</span>
      <input
        className="f-input"
        value={form.vendor}
        placeholder={isMobile ? 'Who you paid' : 'e.g. Kayell Australia'}
        onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))}
        autoFocus={!isMobile}
      />
    </>
  );
  const categoryField = (
    <>
      <span className="f-label">Category</span>
      <SelectField
        label="Category"
        items={EXPENSE_CATEGORIES.map((c) => ({ value: c, label: categoryLabel(c) }))}
        value={form.category as (typeof EXPENSE_CATEGORIES)[number]}
        onChange={(category) => setForm((f) => ({ ...f, category }))}
      />
    </>
  );
  const descriptionField = (
    <>
      <span className="f-label">Description</span>
      <input
        className="f-input"
        value={form.description}
        placeholder="What you bought and why"
        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
      />
    </>
  );
  const amountInput = (
    <input
      className={isMobile ? 'f-input money exp-amount' : 'f-input money'}
      value={form.amount}
      placeholder="$0.00"
      inputMode={isMobile ? 'decimal' : undefined}
      onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
    />
  );
  const amountField = (
    <>
      <span className="f-label">
        Amount <span className="req">*</span>
      </span>
      {isMobile ? (
        // The GST split rides inside the field, where the mockup puts it.
        <span className="exp-amount-wrap">
          {amountInput}
          {cents !== null && form.gstMode === 'incl' && (
            <span className="exp-amount-gst">GST {formatMoneyExact(gstFromGross(cents))}</span>
          )}
        </span>
      ) : (
        amountInput
      )}
    </>
  );
  const gstField = (
    <>
      <span className="f-label">GST</span>
      <Seg
        options={[
          { value: 'incl', label: 'Incl. 10%' },
          { value: 'free', label: 'GST-free' },
        ]}
        value={form.gstMode}
        onChange={(gstMode) => setForm((f) => ({ ...f, gstMode }))}
      />
    </>
  );
  const dateField = (
    <>
      <span className="f-label">Date</span>
      <DateField
        label="Date"
        value={form.expense_date}
        onChange={(expense_date) => setForm((f) => ({ ...f, expense_date }))}
      />
    </>
  );
  const projectField = (
    <>
      <span className="f-label">Project</span>
      <SelectField
        label="Project"
        items={projects.map((p) => ({ value: p.id, label: p.name }))}
        value={form.project_id}
        onChange={(project_id) => setForm((f) => ({ ...f, project_id }))}
        placeholder="Link to project…"
        searchable
      />
    </>
  );
  const receiptField = (
    <>
      <span className="f-label">Receipt</span>
      {isMobile ? (
        <span className="exp-receipt">
          <input
            className={`f-input mono${form.receipt_url ? ' acc' : ''}`}
            value={form.receipt_url}
            placeholder="Paste receipt URL…"
            inputMode="url"
            onChange={(e) => setForm((f) => ({ ...f, receipt_url: e.target.value }))}
          />
          {form.receipt_url && (
            <a
              className="exp-receipt-open"
              href={form.receipt_url}
              target="_blank"
              rel="noreferrer"
              aria-label="Open the receipt"
            >
              ↗
            </a>
          )}
        </span>
      ) : (
        <input
          className="f-input mono"
          value={form.receipt_url}
          placeholder="Paste receipt URL…"
          onChange={(e) => setForm((f) => ({ ...f, receipt_url: e.target.value }))}
        />
      )}
    </>
  );

  return (
    <>
    <Modal
      mobile="full"
      title={initial ? 'Edit expense' : 'New expense'}
      context={isMobile ? 'GST splits out automatically for BAS' : 'FINANCE'}
      onClose={onClose}
      hint={initial || isMobile ? undefined : 'GST splits out automatically for BAS'}
      foot={
        <>
          {/* On mobile the delete is a row in the body: the sheet footer only
              maps a clean Cancel / primary pair. */}
          {initial && !isMobile && (
            <span style={{ marginRight: 'auto' }}>
              <TrashAction
                label="Move to trash"
                onConfirm={() => trash().catch((err) => reportError(err, 'Could not move the expense to trash.'))}
              />
            </span>
          )}
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={submit} disabled={busy || cents === null || !form.expense_date}>
            {initial ? 'Save expense →' : 'Add expense →'}
          </button>
        </>
      }
    >
      {isMobile ? (
        <div className="form-grid exp-form">
          <div className="f-field">{amountField}</div>
          <div className="f-field">{gstField}</div>
          <div className="f-field exp-pair">{vendorField}</div>
          <div className="f-field exp-pair">{categoryField}</div>
          <div className="f-field">{descriptionField}</div>
          <div className="f-field exp-pair">{dateField}</div>
          <div className="f-field exp-pair">{projectField}</div>
          <div className="f-field">{receiptField}</div>
          {error && <p className="error-text span-2">{error}</p>}
          {initial && (
            <div className="exp-danger">
              <SheetRow
                icon={<TrashIcon />}
                label="Move to trash"
                tone="danger"
                onSelect={() => {
                  setTrashError(null);
                  setConfirmTrash(true);
                }}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="form-grid">
          <div className="f-field">{vendorField}</div>
          <div className="f-field">{categoryField}</div>
          <div className="f-field span-2">{descriptionField}</div>
          <div className="f-field">{amountField}</div>
          <div className="f-field">{gstField}</div>
          <div className="f-field">{dateField}</div>
          <div className="f-field">{projectField}</div>
          <div className="f-field span-2">{receiptField}</div>
          {error && <p className="error-text span-2">{error}</p>}
        </div>
      )}
    </Modal>
    {confirmTrash && initial && (
      <Sheet
        open
        variant="half"
        title="Move expense to trash?"
        context={initial.vendor ?? initial.description ?? categoryLabel(initial.category)}
        onClose={() => {
          if (!trashBusy) setConfirmTrash(false);
        }}
        footer={
          <>
            <button type="button" className="btn" disabled={trashBusy} onClick={() => setConfirmTrash(false)}>
              Cancel
            </button>
            <button type="button" className="btn danger" disabled={trashBusy} onClick={() => void confirmTrashMobile()}>
              {trashBusy ? 'Moving…' : 'Move to trash'}
            </button>
          </>
        }
      >
        <p className="fin-confirm">Trashed expenses stay recoverable from Settings → Trash.</p>
        {trashError && (
          <p className="fin-confirm error" role="alert">
            {trashError}
          </p>
        )}
      </Sheet>
    )}
    </>
  );
}
